/**
 * Picture helpers for the support thread (the header's headset modal and the root-admin inbox):
 * grabbing the visible screen, and turning a pasted / picked image into the same compact jpeg.
 *
 * html2canvas141 (html2canvas 1.4.1) on purpose — the hoisted 1.1.4 ($html2canvas) drops spaces
 * and reorders digits in Hebrew, which is exactly what a support screenshot must not do.
 */
import html2canvas141 from 'html2canvas141';

//wider than this is downscaled: a 4K screen at scale 1 would be a ~3MB jpeg for no gain
const MAX_WIDTH = 1600;

export default {

    //the visible viewport as a jpeg data url. `ignore` gets every element html2canvas walks and
    //answers true for the ones to leave out (the support modal itself and its backdrop)
    captureViewport(ignore) {

        return html2canvas141(document.body, {
            useCORS: true,
            logging: false,
            scale: 1,
            x: window.scrollX,
            y: window.scrollY,
            width: window.innerWidth,
            height: window.innerHeight,
            windowWidth: document.documentElement.clientWidth,
            windowHeight: document.documentElement.clientHeight,
            ignoreElements: element => !!(ignore && ignore(element)),
        }).then(canvas => this.canvasToJpeg(canvas));

    },

    canvasToJpeg(canvas) {

        let width = canvas.width;
        let height = canvas.height;

        if (width > MAX_WIDTH) {
            height = Math.round(height * MAX_WIDTH / width);
            width = MAX_WIDTH;
        }

        let output = document.createElement('canvas');
        output.width = width;
        output.height = height;

        let context = output.getContext('2d');
        //jpeg has no alpha - transparent areas would come out black
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        context.drawImage(canvas, 0, 0, width, height);

        return output.toDataURL('image/jpeg', 0.82);

    },

    //a pasted / picked image file → jpeg data url, downscaled the same way
    fileToJpeg(file) {

        return new Promise((resolve, reject) => {

            if (!file || !/^image\//.test(file.type || '')) {
                reject(new Error('not an image'));
                return;
            }

            let reader = new FileReader();

            reader.onload = event => {

                let image = new Image();

                image.onload = () => {
                    let canvas = document.createElement('canvas');
                    canvas.width = image.naturalWidth;
                    canvas.height = image.naturalHeight;
                    canvas.getContext('2d').drawImage(image, 0, 0);
                    resolve(this.canvasToJpeg(canvas));
                };

                image.onerror = () => reject(new Error('bad image'));
                image.src = event.target.result;

            };

            reader.onerror = () => reject(new Error('read failed'));
            reader.readAsDataURL(file);

        });

    },

    //the image file in a paste event, if the clipboard holds one (a PrtScn / Cmd-Shift-4 capture)
    imageFromPaste(event) {

        let items = event && event.clipboardData && event.clipboardData.items;

        if (!items)
            return null;

        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file' && /^image\//.test(items[i].type || ''))
                return items[i].getAsFile();
        }

        return null;

    },

    escapeHtml(text) {
        return String(text === 0 || text ? text : '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    },

    //"היום 14:32" / "12/8 14:32" — what a chat bubble needs, nothing more
    timeLabel(created) {

        if (!created)
            return '';

        let date = new Date(created);

        if (isNaN(date.getTime()))
            return '';

        let hh = String(date.getHours()).padStart(2, '0');
        let mm = String(date.getMinutes()).padStart(2, '0');

        let today = new Date();
        let sameDay = date.getFullYear() == today.getFullYear() && date.getMonth() == today.getMonth() && date.getDate() == today.getDate();

        if (sameDay)
            return `${hh}:${mm}`;

        return `${date.getDate()}/${date.getMonth() + 1} ${hh}:${mm}`;

    },

};
